import React, { createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import apiOffers from 'api/offers';
import translations from 'services/translations/translations';
import numericStringToFloat from 'services/numbers/numericStringToFloat';

import formatToFormStateFormat from '../formatToFormStateFormat';
import IFormState from '../IFormState';
import IInstallation from '../IInstallation';
import ISubproduct from '../ISubproduct';
import IVersion from '../IVersion';
import Installation from './installations/Installation';
import Subproduct from './subproducts/Subproduct';
import Summary from './Summary';

import 'style/offers/PositionHistory.css';

/** The same position on the preliminary offer or another version of the offer. */
export interface IPositionHistoryEntry {
  isPreliminaryOffer: boolean;
  position: IFormState;
  revisionNumber: number;
}

/**
 * The positions shown under the rows of the calculation window and the name of the
 * alternative being calculated; null when they are not shown.
 */
export const PositionHistoryContext = createContext<{
  entries: IPositionHistoryEntry[];
  versionName: string;
} | null>(null);

export const loadPositionHistory = async (
  offerProductId: number
): Promise<IPositionHistoryEntry[]> => {
  const history = await apiOffers.products.history(offerProductId);

  return Promise.all(
    history.map(async (entry) => ({
      isPreliminaryOffer: entry.isPreliminaryOffer,
      position: formatToFormStateFormat(
        await apiOffers.products.get(entry.offerProductId)
      ),
      revisionNumber: entry.revisionNumber,
    }))
  );
};

// The alternative of the same name, otherwise the primary one
const getVersion = (
  position: IFormState,
  versionName: string
): IVersion | undefined =>
  position.versions.find((version) => version.name === versionName) ??
  position.versions.find((version) => version.isPrimary) ??
  position.versions[0];

const getSubproductKey = (subproduct: ISubproduct) =>
  `${subproduct.category}|${
    subproduct.isCustomSubproduct
      ? `custom:${subproduct.customSubproduct}`
      : subproduct.product?.value ?? ''
  }`;

const getInstallationKey = (installation: IInstallation) =>
  `${installation.category}|${
    installation.isCustomInstallation
      ? `custom:${installation.customInstallation}`
      : installation.installation?.value ?? ''
  }`;

// The row of the other position that is the same as the row: the same product (or the
// same typed name) in the same category; the second such row matches the second one
const findSameRow = <T extends { key: number }>(
  rows: T[],
  row: T,
  otherRows: T[],
  getKey: (row: T) => string
): T | undefined => {
  const key = getKey(row);
  const occurrence = rows
    .slice(
      0,
      rows.findIndex((existing) => existing.key === row.key)
    )
    .filter((existing) => getKey(existing) === key).length;

  return otherRows.filter((other) => getKey(other) === key)[occurrence];
};

const noop = () => undefined;

const HistoryRow = ({
  children,
  entry,
}: {
  children: React.ReactNode;
  entry: IPositionHistoryEntry;
}): JSX.Element => {
  const { t } = useTranslation([translations.offers]);

  return (
    <div className="position-history-row">
      <span className="position-history-label">
        {t(
          `${translations.calculationWindow}:${
            entry.isPreliminaryOffer
              ? 'History preliminary offer'
              : 'History version'
          }`,
          { number: entry.revisionNumber }
        )}
      </span>
      {children}
    </div>
  );
};

/** The same material row on the preliminary offer and the other versions. */
export const SubproductHistoryRows = ({
  subproduct,
  subproducts,
}: {
  subproduct: ISubproduct;
  subproducts: ISubproduct[];
}): JSX.Element | null => {
  const history = useContext(PositionHistoryContext);
  if (history === null) {
    return null;
  }

  return (
    <>
      {history.entries.map((entry) => {
        const version = getVersion(entry.position, history.versionName);
        const same =
          version === undefined
            ? undefined
            : findSameRow(
                subproducts,
                subproduct,
                version.subproducts,
                getSubproductKey
              );

        return same === undefined ? null : (
          <HistoryRow
            entry={entry}
            key={`${entry.position.position}-${entry.revisionNumber}-${entry.isPreliminaryOffer}`}
          >
            <Subproduct
              locked
              onRemoveDetail={noop}
              opened={false}
              quantity={numericStringToFloat(entry.position.quantity)}
              reordering={false}
              removeSubproduct={noop}
              setSubproduct={noop}
              subproduct={same}
              targetMarginPercent=""
              toggleOpened={noop}
            />
          </HistoryRow>
        );
      })}
    </>
  );
};

/**
 * The summary of the calculation (selling price, expenses, margin of the whole position)
 * on the preliminary offer and the other versions.
 */
export const SummaryHistoryRows = (): JSX.Element | null => {
  const history = useContext(PositionHistoryContext);
  if (history === null) {
    return null;
  }

  return (
    <>
      {history.entries.map((entry) => {
        const version = getVersion(entry.position, history.versionName);

        return version === undefined ? null : (
          <HistoryRow
            entry={entry}
            key={`${entry.position.position}-${entry.revisionNumber}-${entry.isPreliminaryOffer}`}
          >
            <Summary
              installations={version.installations}
              productUnit={entry.position.unit}
              quantity={numericStringToFloat(entry.position.quantity)}
              subproducts={version.subproducts}
            />
          </HistoryRow>
        );
      })}
    </>
  );
};

/** The same installation or other expense on the preliminary offer and the other versions. */
export const InstallationHistoryRows = ({
  installation,
  installations,
  type,
}: {
  installation: IInstallation;
  installations: IInstallation[];
  type: string;
}): JSX.Element | null => {
  const history = useContext(PositionHistoryContext);
  if (history === null) {
    return null;
  }

  return (
    <>
      {history.entries.map((entry) => {
        const version = getVersion(entry.position, history.versionName);
        const same =
          version === undefined
            ? undefined
            : findSameRow(
                installations,
                installation,
                version.installations,
                getInstallationKey
              );

        return same === undefined ? null : (
          <HistoryRow
            entry={entry}
            key={`${entry.position.position}-${entry.revisionNumber}-${entry.isPreliminaryOffer}`}
          >
            <Installation
              installation={same}
              locked
              quantity={numericStringToFloat(entry.position.quantity)}
              removeInstallation={noop}
              setInstallation={noop}
              targetMarginPercent=""
              type={type}
            />
          </HistoryRow>
        );
      })}
    </>
  );
};
